import { useState, useEffect, useRef, useCallback } from 'react'
import { allExplorePages, ExploreItem } from './mockData'

// Fisher-Yates shuffle algorithm
const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array]
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[shuffled[i], shuffled[j]] = [shuffled[j]!, shuffled[i]!]
  }
  return shuffled
}

const ITEMS_PER_PAGE = 12
const GRID_ROW_HEIGHT = 8
const GRID_GAP = 16

export default function ExploreSection() {
  // Store all items shuffled once on mount - this persists for the session
  // Flattening ensures better global randomness compared to shuffling chunks
  const [allShuffledItems] = useState<ExploreItem[]>(() => shuffleArray(allExplorePages.flat()))

  // Store loaded pages as separate arrays to maintain layout stability
  // Using array of arrays prevents CSS column reflow of previous items
  const [loadedPages, setLoadedPages] = useState<ExploreItem[][]>([])
  const [nextPageIndex, setNextPageIndex] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const loaderRef = useRef<HTMLDivElement>(null)

  // Load first page immediately on mount
  useEffect(() => {
    if (loadedPages.length === 0 && allShuffledItems.length > 0) {
      const firstPage = allShuffledItems.slice(0, ITEMS_PER_PAGE)
      setLoadedPages([firstPage])
      setNextPageIndex(1)
    }
  }, [allShuffledItems, loadedPages.length])

  const loadNextPage = useCallback(() => {
    const startIdx = nextPageIndex * ITEMS_PER_PAGE
    if (startIdx >= allShuffledItems.length) return

    setIsLoading(true)
    // Simulate loading delay
    setTimeout(() => {
      const endIdx = startIdx + ITEMS_PER_PAGE
      const newPage = allShuffledItems.slice(startIdx, endIdx)

      if (newPage.length > 0) {
        setLoadedPages((prev) => [...prev, newPage])
        setNextPageIndex((prev) => prev + 1)
      }
      setIsLoading(false)
    }, 500)
  }, [nextPageIndex, allShuffledItems])

  // Intersection Observer for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0]
        if (target && target.isIntersecting && !isLoading) {
          loadNextPage()
        }
      },
      {
        threshold: 0.1,
        rootMargin: '100px',
      }
    )

    const currentLoader = loaderRef.current
    if (currentLoader) {
      observer.observe(currentLoader)
    }

    return () => {
      if (currentLoader) {
        observer.unobserve(currentLoader)
      }
    }
  }, [isLoading, nextPageIndex, allShuffledItems, loadNextPage])

  const hasMore = nextPageIndex * ITEMS_PER_PAGE < allShuffledItems.length

  return (
    <div className="h-auto rounded-t-[32px] bg-white pt-4 px-6 pb-6">
      <h2 className="mb-4 text-lg font-semibold text-gray-900">Explore</h2>

      {/* 
        Render each page in its own masonry grid container.
        Row spans are measured per card to keep 9:16 and 16:9 cards tightly packed.
      */}
      {loadedPages.map((pageItems, pageIndex) => (
        <div
          key={pageIndex}
          className="mb-4 grid grid-flow-dense grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4"
          style={{ gridAutoRows: `${GRID_ROW_HEIGHT}px` }}
        >
          {pageItems.map((item) => (
            <ExploreCard key={item.id} item={item} />
          ))}
        </div>
      ))}

      {/* Loading indicator */}
      <div ref={loaderRef} className="mt-8 flex justify-center py-4">
        {isLoading && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
            <span>Loading more...</span>
          </div>
        )}
        {!isLoading && !hasMore && <p className="text-sm text-gray-400">No more items</p>}
      </div>
    </div>
  )
}

function ExploreCard({ item }: { item: ExploreItem }) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const cardRef = useRef<HTMLDivElement>(null)
  const [rowSpan, setRowSpan] = useState(1)
  const aspectRatio = item.aspectRatio === '16:9' ? 9 / 16 : 16 / 9
  const aspectRatioClass = item.aspectRatio === '16:9' ? 'aspect-video' : 'aspect-[9/16]'

  useEffect(() => {
    const element = cardRef.current
    if (!element) return

    const updateSpan = () => {
      const width = element.getBoundingClientRect().width
      if (!width) return
      const height = width * aspectRatio
      const span = Math.max(1, Math.ceil((height + GRID_GAP) / (GRID_ROW_HEIGHT + GRID_GAP)))
      setRowSpan(span)
    }

    updateSpan()
    const resizeObserver = new ResizeObserver(() => updateSpan())
    resizeObserver.observe(element)

    return () => resizeObserver.disconnect()
  }, [aspectRatio])

  const handlePointerEnter = () => {
    if (item.type !== 'video') return
    const video = videoRef.current
    if (!video) return

    video.play().catch(() => {
      // Autoplay might be blocked by browser policies.
    })
  }

  const handlePointerLeave = () => {
    if (item.type !== 'video') return
    const video = videoRef.current
    if (!video) return

    video.pause()
    video.currentTime = 0
  }

  return (
    <div
      ref={cardRef}
      style={{ gridRowEnd: `span ${rowSpan}` }}
      className="group relative cursor-pointer overflow-hidden rounded-lg"
      onMouseEnter={handlePointerEnter}
      onMouseLeave={handlePointerLeave}
    >
      <div className={`relative ${aspectRatioClass} bg-gray-100`}>
        {item.type === 'video' && item.videoUrl ? (
          <video
            ref={videoRef}
            src={item.videoUrl}
            poster={item.imageUrl}
            muted
            loop
            playsInline
            preload="metadata"
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <img
            src={item.imageUrl}
            alt={item.title}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
          />
        )}
      </div>
    </div>
  )
}
